import { api, ApiError, USE_MOCK_DATA } from '@/lib/api';
import { seedCategoryList, type LeadCategoryItem, type LeadCategoryList } from '@/data/leads-model';

/* ----------------------------------------------------------------------------
 * Lead categories — the workspace-managed list (item 7, owner addition 05-10-2026).
 *
 *   fetchLeadCategories()                 GET   /outreach/lead-categories            (any member)
 *   addLeadCategory(name)                 POST  /outreach/lead-categories            (owner)
 *   updateLeadCategory(slug, {name?, status?})  PATCH /outreach/lead-categories/{slug}  (owner: rename / archive / restore)
 *   mergeLeadCategory(slug, into)         POST  /outreach/lead-categories/{slug}/merge  (owner)
 *   resyncLeadCategories()                POST  /outreach/lead-categories/resync     (owner: adds new booksesh.com categories)
 *
 * The server is the authority (owner-only, CSRF, audit, never deletes). Every
 * mutation answers with the fresh list, so callers just replace their copy.
 * -------------------------------------------------------------------------- */

export interface LeadCategoryMutation extends LeadCategoryList { item?: LeadCategoryItem | null }
export interface LeadCategoryResync extends LeadCategoryList {
  resync: { ok: boolean; source: 'live' | 'partial' | 'snapshot'; sources?: Record<string, 'live' | 'snapshot'>; added: LeadCategoryItem[]; reused?: Array<{ term: string; name: string; existing: string }>; kept: number; terms: number; error?: string };
}

let mock: LeadCategoryList | null = null;
const mockList = (): LeadCategoryList => (mock ??= seedCategoryList());
const nameKey = (n: string) => n.trim().toLowerCase().replace(/&/g, ' and ').replace(/\s+/g, ' ').trim();
const slugify = (name: string) => name.trim().toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

export async function fetchLeadCategories(): Promise<LeadCategoryList> {
  if (USE_MOCK_DATA) return structuredClone(mockList());
  return api.get<LeadCategoryList>('/outreach/lead-categories');
}

export async function addLeadCategory(name: string): Promise<LeadCategoryMutation> {
  if (USE_MOCK_DATA) {
    const l = mockList(); const slug = slugify(name);
    if (!slug) throw new ApiError(400, 'validation_failed', 'Give the category a name.');
    if (l.items.some((i) => i.slug === slug || nameKey(i.name) === nameKey(name))) throw new ApiError(409, 'duplicate_category', `"${name.trim()}" already exists.`);
    const item: LeadCategoryItem = { slug, name: name.trim(), source: 'workspace', status: 'active', count: 0 };
    l.items.splice(l.items.length - 1, 0, item); l.is_default = false;
    return { ...structuredClone(l), item };
  }
  return api.post<LeadCategoryMutation>('/outreach/lead-categories', { name });
}

export async function updateLeadCategory(slug: string, body: { name?: string; status?: 'active' | 'archived' }): Promise<LeadCategoryMutation> {
  if (USE_MOCK_DATA) {
    const l = mockList(); const it = l.items.find((i) => i.slug === slug);
    if (!it) throw new ApiError(404, 'not_found', 'Unknown category.');
    if (body.name) it.name = body.name.trim();
    if (body.status) { it.status = body.status; if (body.status === 'active') delete it.merged_into; }
    l.is_default = false;
    return { ...structuredClone(l), item: it };
  }
  return api.patch<LeadCategoryMutation>(`/outreach/lead-categories/${encodeURIComponent(slug)}`, body);
}

export async function mergeLeadCategory(slug: string, into: string): Promise<LeadCategoryMutation> {
  if (USE_MOCK_DATA) {
    const l = mockList(); const it = l.items.find((i) => i.slug === slug);
    if (!it) throw new ApiError(404, 'not_found', 'Unknown category.');
    it.status = 'archived'; it.merged_into = into; l.is_default = false;
    return { ...structuredClone(l), item: it };
  }
  return api.post<LeadCategoryMutation>(`/outreach/lead-categories/${encodeURIComponent(slug)}/merge`, { into });
}

export async function resyncLeadCategories(): Promise<LeadCategoryResync> {
  if (USE_MOCK_DATA) return { ...structuredClone(mockList()), resync: { ok: true, source: 'snapshot', added: [], reused: [], kept: mockList().items.length, terms: 20, error: 'dev mock — no network' } };
  return api.post<LeadCategoryResync>('/outreach/lead-categories/resync', {});
}
